/**
 * QuestionView Component
 * Displays a single exam question with answer options
 */
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Check, X } from 'lucide-react';

const QuestionView = ({
  question,
  questionIndex,
  totalQuestions,
  selectedAnswers,
  onAnswer
}) => {
  if (!question) return null;

  // Convert c0/c1/c2 to A/B/C format
  const convertChoiceId = (id, idx) => {
    if (!id) return String.fromCharCode(65 + idx); // A, B, C...
    // Handle c0, c1, c2 format
    if (typeof id === 'string' && /^c\d+$/.test(id)) {
      const num = parseInt(id.substring(1), 10);
      return String.fromCharCode(65 + num); // c0 -> A, c1 -> B, etc.
    }
    return id;
  };

  // Normalize choices - handle both array of strings and array of objects
  const normalizeChoices = (choices) => {
    if (!choices || !Array.isArray(choices)) return [];
    return choices.map((choice, idx) => {
      if (typeof choice === 'string') {
        return { id: String.fromCharCode(65 + idx), text: choice };
      }
      const rawId = choice.id || choice.choice_id;
      return {
        id: convertChoiceId(rawId, idx),
        text: choice.text || choice.choice_text || choice
      };
    });
  };

  const choices = normalizeChoices(question.choices);
  const questionText = question.question_text || question.questionText || '';

  return (
    <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <span className="text-[#0047AB]">Question {questionIndex + 1}</span>
          <span className="text-slate-400 font-normal text-sm">of {totalQuestions}</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-slate-800 dark:text-slate-200 mb-6 whitespace-pre-wrap leading-relaxed">
          {questionText}
        </p>

        {/* Single Choice */}
        {question.question_type === 'single' && (
          <RadioGroup 
            value={selectedAnswers?.[0] || ''} 
            onValueChange={(value) => onAnswer(value)}
            className="space-y-3"
          >
            {choices.map((choice) => (
              <Label
                key={choice.id}
                htmlFor={`choice-${choice.id}`}
                className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  selectedAnswers?.includes(choice.id)
                    ? 'border-[#0047AB] bg-blue-50 dark:bg-blue-950/30'
                    : 'border-slate-200 dark:border-slate-700 hover:border-[#0047AB]/50'
                }`}
              >
                <RadioGroupItem value={choice.id} id={`choice-${choice.id}`} />
                <span className="flex-1">{choice.text}</span>
              </Label>
            ))}
          </RadioGroup>
        )}

        {/* Multiple Choice */}
        {question.question_type === 'multi' && (
          <div className="space-y-3">
            <p className="text-sm text-slate-500 mb-2">Select all that apply</p>
            {choices.map((choice) => (
              <Label
                key={choice.id}
                htmlFor={`choice-${choice.id}`}
                className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  selectedAnswers?.includes(choice.id)
                    ? 'border-[#0047AB] bg-blue-50 dark:bg-blue-950/30'
                    : 'border-slate-200 dark:border-slate-700 hover:border-[#0047AB]/50'
                }`}
              >
                <Checkbox 
                  id={`choice-${choice.id}`}
                  checked={selectedAnswers?.includes(choice.id)}
                  onCheckedChange={() => onAnswer(choice.id)}
                />
                <span className="flex-1">{choice.text}</span>
              </Label>
            ))}
          </div>
        )}

        {/* True/False */}
        {question.question_type === 'truefalse' && (
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => onAnswer('true')}
              className={`p-6 rounded-xl border-2 flex flex-col items-center gap-3 transition-all ${
                selectedAnswers?.[0] === 'true'
                  ? 'border-green-500 bg-green-50 dark:bg-green-950/30'
                  : 'border-slate-200 dark:border-slate-700 hover:border-green-500/50'
              }`}
            >
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                selectedAnswers?.[0] === 'true' 
                  ? 'bg-green-500 text-white' 
                  : 'bg-green-100 dark:bg-green-900/30 text-green-600'
              }`}>
                <Check className="w-6 h-6" />
              </div>
              <span className="font-semibold text-lg">True</span>
            </button>
            
            <button
              onClick={() => onAnswer('false')}
              className={`p-6 rounded-xl border-2 flex flex-col items-center gap-3 transition-all ${
                selectedAnswers?.[0] === 'false'
                  ? 'border-red-500 bg-red-50 dark:bg-red-950/30'
                  : 'border-slate-200 dark:border-slate-700 hover:border-red-500/50'
              }`}
            >
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                selectedAnswers?.[0] === 'false' 
                  ? 'bg-red-500 text-white' 
                  : 'bg-red-100 dark:bg-red-900/30 text-red-600'
              }`}>
                <X className="w-6 h-6" />
              </div>
              <span className="font-semibold text-lg">False</span>
            </button>
          </div>
        )}

        {/* Fill in the blank */}
        {question.question_type === 'fill' && (
          <div>
            <Label htmlFor="fill-answer" className="mb-2 block text-sm text-slate-600">
              Enter your answer
            </Label>
            <Input
              id="fill-answer"
              placeholder="Type your answer..."
              value={selectedAnswers?.[0] || ''}
              onChange={(e) => onAnswer(e.target.value)}
              className="max-w-md"
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default QuestionView;
